import numbro from 'numbro';
import { BytesUnitKind, BytesUnitOptions } from '../schema';
import { FormatValueOptions, UnitConfig, UnitGroupConfig } from './types';
import { hasDecimalPlaces, limitDecimalPlaces, SCIENTIFIC_NOTATION_FORMATTER, shouldAbbreviate } from './utils';

/**
 * We consider the units for bytes to be powers of 1000.
 * In other words:
 * 1 KB = 1000 bytes (1000^1 bytes)
 * 1 MB = 1,000,000 bytes (1000^2 bytes)
 * etc.
 */

const DEFAULT_DECIMAL_PLACES = 2;
const TINY_BYTES_THRESHOLD = 0.0001;

const BYTES_GROUP = 'Bytes';
export const BYTES_GROUP_CONFIG: UnitGroupConfig = {
  label: 'Bytes',
  decimal_places: true,
  abbreviate: true,
};
export const BYTES_UNIT_CONFIG: Readonly<Record<BytesUnitKind, UnitConfig>> = {
  Bytes: {
    group: BYTES_GROUP,
    label: 'Bytes (decimal)',
    description: 'KB, MB, GB, etc.',
  },
  BinaryBytes: {
    group: BYTES_GROUP,
    label: 'Bytes (binary)',
    description: 'KiB, MiB, GiB, etc.',
  },
  BytesPerSec: {
    group: BYTES_GROUP,
    label: 'Bytes/sec (decimal)',
    description: 'KB/s, MB/s, GB/s, etc.',
  },
  BinaryBytesPerSec: {
    group: BYTES_GROUP,
    label: 'Bytes/sec (binary)',
    description: 'KiB/s, MiB/s, GiB/s, etc.',
  },
};

type FormatBytesOptions = BytesUnitOptions & Pick<FormatValueOptions, 'trimZeros' | 'scientificForTinyValues'>;

export function formatBytes(
  bytes: number,
  { kind, abbreviate, decimal_places, trimZeros = true, scientificForTinyValues = false }: FormatBytesOptions
) {
  const isPerSec = kind === 'BytesPerSec' || kind === 'BinaryBytesPerSec';
  let baseKind: 'Bytes' | 'BinaryBytes' | typeof kind = kind;
  if (isPerSec) {
    baseKind = kind === 'BytesPerSec' ? 'Bytes' : 'BinaryBytes';
  }
  const perSecSuffix = isPerSec ? '/s' : '';

  const isDecimalBytes = baseKind === 'Bytes';
  const isBinaryBytes = baseKind === 'BinaryBytes';
  const isVerySmallValue = Math.abs(bytes) < TINY_BYTES_THRESHOLD;

  if (scientificForTinyValues && bytes !== 0 && isVerySmallValue) {
    return `${SCIENTIFIC_NOTATION_FORMATTER.format(bytes)} bytes${perSecSuffix}`;
  }

  // If `abbreviate: false`, use Intl.NumberFormat.
  // If value < 1000 (decimal) or 1024 (binary), use Intl.NumberFormat so that it says "bytes" instead of "B" (which can be confused with 1 billion)
  if (
    !shouldAbbreviate(abbreviate) ||
    (isDecimalBytes && Math.abs(bytes) < 1000) ||
    (isBinaryBytes && Math.abs(bytes) < 1024)
  ) {
    const formatterOptions: Intl.NumberFormatOptions = {
      style: 'unit',
      unit: 'byte',
      unitDisplay: 'long',
      useGrouping: true,
    };

    if (hasDecimalPlaces(decimal_places)) {
      formatterOptions.maximumFractionDigits = limitDecimalPlaces(decimal_places);
      if (!trimZeros) {
        formatterOptions.minimumFractionDigits = limitDecimalPlaces(decimal_places);
      }
    } else {
      if (Math.abs(bytes) < 1) {
        // For values < 1, allow up to 3 significant digits
        formatterOptions.maximumSignificantDigits = 3;
      } else {
        // For values >= 1, allow up to 2 decimal places.
        formatterOptions.maximumFractionDigits = DEFAULT_DECIMAL_PLACES;
      }
    }

    const formatter = Intl.NumberFormat('en-US', formatterOptions);
    return formatter.format(bytes) + perSecSuffix;
  }

  // If `abbreviate: true`, use numbro.
  // numbro is able to add units like KB, MB, GB, etc. correctly.
  const formatted = numbro(bytes).format({
    output: 'byte',
    base: isDecimalBytes ? 'decimal' : 'binary',
    spaceSeparated: true,
    mantissa: hasDecimalPlaces(decimal_places) ? decimal_places : DEFAULT_DECIMAL_PLACES,
    // trimMantissa trims trailing 0s
    trimMantissa: trimZeros,
    // optionalMantissa excludes all the decimal places if they're all zeros
    optionalMantissa: trimZeros,
  });
  return formatted + perSecSuffix;
}
